import fs from 'fs/promises'
import path from 'path'
import chalk from 'chalk'
import { z } from 'zod'

interface CheckOptions {
  schema: string
  strict: boolean
  debug: boolean
}

export async function check(options: CheckOptions) {
  const { schema, strict, debug } = options

  console.log(chalk.blue('🔍 Checking current process environment...\n'))

  try {
    // Check if schema file exists
    await fs.access(schema)
  } catch {
    throw new Error(`Schema file not found: ${schema}`)
  }

  // Load the schema
  const { loadSchemaFile } = await import('./utils')
  const schemaModule = await loadSchemaFile(schema)
  if (!schemaModule.env) {
    throw new Error(`No 'env' export found in ${schema}`)
  }

  // Create the proper Zod schema from the raw structure
  const schemaDef = schemaModule.env
  const envSchema = z.object({
    ...(schemaDef.client || {}),
    ...(schemaDef.server || {})
  })

  console.log(chalk.gray(`📁 Schema: ${schema}`))
  console.log(chalk.gray(`🔧 Strict mode: ${strict ? 'enabled' : 'disabled'}`))
  console.log(chalk.gray(`🐛 Debug mode: ${debug ? 'enabled' : 'disabled'}`))
  console.log('')

  // Validate the current process environment
  try {
    const validatedEnv = envSchema.parse(process.env)
    
    console.log(chalk.green('✅ Process environment validation passed!'))
    
    if (debug) {
      console.log(chalk.blue('\n📊 Validated environment variables:'))
      Object.entries(validatedEnv).forEach(([key, value]) => {
        const displayValue = typeof value === 'string' && value.length > 50 
          ? `${value.substring(0, 50)}...` 
          : value
        console.log(chalk.gray(`  ${key}: ${displayValue}`))
      })
    }

    // Check for missing required variables
    const missingVars: string[] = []
    
    if (schemaDef.client) {
      Object.keys(schemaDef.client).forEach(key => {
        if (!process.env[key] && !schemaDef.client[key]._def.defaultValue) {
          missingVars.push(key)
        }
      })
    }
    
    if (schemaDef.server) {
      Object.keys(schemaDef.server).forEach(key => {
        if (!process.env[key] && !schemaDef.server[key]._def.defaultValue) {
          missingVars.push(key)
        }
      })
    }

    if (missingVars.length > 0) {
      console.log(chalk.yellow('\n⚠️  Missing environment variables:'))
      missingVars.forEach(varName => {
        console.log(chalk.yellow(`  - ${varName}`))
      })
      console.log(chalk.gray('\nSet these environment variables or provide default values in your schema.'))
    }

    // Check for unknown variables in strict mode
    if (strict) {
      const schemaKeys = new Set([
        ...(schemaDef.client ? Object.keys(schemaDef.client) : []),
        ...(schemaDef.server ? Object.keys(schemaDef.server) : [])
      ])
      
      const unknownVars = Object.keys(process.env).filter(key => !schemaKeys.has(key))
      
      if (unknownVars.length > 0) {
        console.log(chalk.yellow('\n⚠️  Unknown environment variables (strict mode):'))
        unknownVars.forEach(varName => {
          console.log(chalk.yellow(`  - ${varName}`))
        })
        console.log(chalk.gray('\nAdd these to your schema or disable strict mode.'))
      }
    }

  } catch (error) {
    console.log(chalk.red('❌ Process environment validation failed!'))
    console.log(chalk.red('\nError details:'))
    
    if (error instanceof Error) {
      console.log(chalk.red(error.message))
    } else {
      console.log(chalk.red(String(error)))
    }
    
    process.exit(1)
  }
} 